import React, { useState, useEffect } from 'react';
import {
  fetchCompanyLimit,
  upsertCompanyLimit,
  fetchCompanyUsageStats,
} from '../../services/companyCustomizationApi';
import type { CompanyLimit, CompanyLimitRequest, UsageStats } from '../../services/companyCustomizationApi';
import { Save, AlertCircle, CheckCircle, TrendingUp, Users, Briefcase, UserCheck } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';

interface CompanyLimitsManagerProps {
  companyId: string;
}

const cardStyle: React.CSSProperties = {
  padding: '20px',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#fff',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151',
};

const CompanyLimitsManager: React.FC<CompanyLimitsManagerProps> = ({ companyId }) => {
  const { t } = useI18n();
  const [limit, setLimit] = useState<CompanyLimit | null>(null);
  const [stats, setStats] = useState<UsageStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState<CompanyLimitRequest>({
    max_users: 10,
    max_employees: 50,
    max_clients: 1000,
    is_active: true,
    notes: '',
  });

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [limitData, statsData] = await Promise.all([
        fetchCompanyLimit(companyId),
        fetchCompanyUsageStats(companyId),
      ]);
      setLimit(limitData);
      setStats(statsData);
      setForm({
        max_users: limitData.max_users,
        max_employees: limitData.max_employees,
        max_clients: limitData.max_clients,
        is_active: limitData.is_active,
        notes: limitData.notes || '',
      });
    } catch (err: unknown) {
      console.error('[CompanyLimitsManager] Failed to load data', err);
      setError(t('limits.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const updated = await upsertCompanyLimit(companyId, form);
      setLimit(updated);
      setMessage('Limites atualizados com sucesso!');
      await loadData();
    } catch (err: unknown) {
      console.error('[CompanyLimitsManager] Failed to save limits', err);
      const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(apiError || 'Não foi possível salvar os limites.');
    } finally {
      setSaving(false);
    }
  };

  const getProgressColor = (percentage: number, isExceeded: boolean) => {
    if (isExceeded) return '#ef4444';
    if (percentage >= 80) return '#f59e0b';
    return '#22c55e';
  };

  const getResourceMeta = (resourceType: string) => {
    switch (resourceType) {
      case 'users':
        return { label: 'Usuários', Icon: Users };
      case 'employees':
        return { label: 'Funcionários', Icon: Briefcase };
      case 'clients':
        return { label: 'Clientes', Icon: UserCheck };
      default:
        return { label: resourceType, Icon: TrendingUp };
    }
  };

  if (loading && !limit) {
    return (
      <div style={{ ...cardStyle, color: '#6b7280', textAlign: 'center' }}>{t('limits.loading')}</div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {message && (
        <div
          role="alert"
          style={{
            padding: '12px 16px',
            backgroundColor: '#dcfce7',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#15803d',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
          }}
        >
          <CheckCircle size={18} />
          {message}
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#b91c1c',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
          }}
        >
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {stats.length > 0 && (
        <div style={cardStyle}>
          <h3
            style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <TrendingUp size={20} color="#2563eb" />{t('limits.usageStats')}</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
            }}
          >
            {stats.map((stat) => {
              const percentage = Math.min(stat.percentage, 100);
              const { label, Icon } = getResourceMeta(stat.resource_type);
              const barColor = getProgressColor(percentage, stat.is_exceeded);

              return (
                <div
                  key={stat.resource_type}
                  style={{
                    padding: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${stat.is_exceeded ? '#fecaca' : '#e5e7eb'}`,
                    backgroundColor: stat.is_exceeded ? '#fef2f2' : '#f9fafb',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '10px',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                      }}
                    >
                      <Icon size={16} />
                      {label}
                    </span>
                    {stat.is_exceeded && <AlertCircle size={16} color="#ef4444" />}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '10px' }}>
                    {stat.current} / {stat.limit}
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '999px',
                      overflow: 'hidden',
                      marginBottom: '6px',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${percentage}%`,
                        backgroundColor: barColor,
                        borderRadius: '999px',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {percentage.toFixed(1)}% utilizado
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={cardStyle}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#111827' }}>{t('limits.configure')}</h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
          }}
        >
          <label style={labelStyle}>
            Máximo de Usuários
            <input
              type="number"
              id="max_users"
              name="max_users"
              min={1}
              value={form.max_users}
              onChange={(e) => setForm({ ...form, max_users: parseInt(e.target.value, 10) || 0 })}
              required
              style={inputStyle}
            />
            {limit && (
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                Atual: {limit.current_users} usuários
              </span>
            )}
          </label>

          <label style={labelStyle}>
            Máximo de Funcionários
            <input
              type="number"
              id="max_employees"
              name="max_employees"
              min={1}
              value={form.max_employees}
              onChange={(e) => setForm({ ...form, max_employees: parseInt(e.target.value, 10) || 0 })}
              required
              style={inputStyle}
            />
            {limit && (
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                Atual: {limit.current_employees} funcionários
              </span>
            )}
          </label>

          <label style={labelStyle}>
            Máximo de Clientes
            <input
              type="number"
              id="max_clients"
              name="max_clients"
              min={1}
              value={form.max_clients}
              onChange={(e) => setForm({ ...form, max_clients: parseInt(e.target.value, 10) || 0 })}
              required
              style={inputStyle}
            />
            {limit && (
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                Atual: {limit.current_clients} clientes
              </span>
            )}
          </label>

          <div style={labelStyle}>
            Status
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 400,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Limites ativos
            </label>
            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>{t('limits.disabledHint')}</span>
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={labelStyle}>
            Notas (opcional)
            <textarea
              id="notes"
              name="notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="submit"
            disabled={saving || loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: saving || loading ? '#93c5fd' : '#2563eb',
              color: '#fff',
              cursor: saving || loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            <Save size={18} />
            {saving ? 'Salvando...' : 'Salvar Limites'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyLimitsManager;
